import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrTrees } from '@/api/ivrTrees';
import type { IvrTreeSummaryRow } from '@/api/ivrTrees';
import { customers } from '@/api/customers';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { CreateTreeDialog } from './CreateTreeDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/cn';
import { routeRef } from '@/lib/formatRef';
import {
  Plus, MoreHorizontal, Trash2, Pencil, ExternalLink,
  AlertTriangle, Phone, GitBranch, Workflow, RotateCcw, Archive,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const CAN_MUTATE: readonly string[] = ['super_admin', 'reseller_owner', 'office_manager'];

// ── Active toggle — switch in a Status column ─────────────────────────────────

function StatusCell({ row, canMutate }: { row: IvrTreeSummaryRow; canMutate: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => ivrTrees.update(row.id, { active: !row.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ivrTrees'] }),
  });
  return (
    <div
      className="inline-flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      <Switch
        checked={row.active}
        onCheckedChange={() => toggle.mutate()}
        disabled={!canMutate || toggle.isPending}
        aria-label={row.active ? t('ivrTrees.deactivateTree') : t('ivrTrees.activateTree')}
      />
      {row.active ? (
        <Badge variant="default" className="text-[10px]">{t('common.active')}</Badge>
      ) : (
        <Badge variant="secondary" className="text-[10px]">{t('common.inactive')}</Badge>
      )}
    </div>
  );
}

// ── Rename dialog ─────────────────────────────────────────────────────────────

function RenameTreeDialog({ row, onClose }: { row: IvrTreeSummaryRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(row.name);

  const rename = useMutation({
    mutationFn: () => ivrTrees.update(row.id, { name: name.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrTrees'] });
      onClose();
    },
  });

  const canSubmit = name.trim().length > 0 && name.trim() !== row.name;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('ivrTrees.renameTitle')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !rename.isPending) rename.mutate();
          }}
          className="space-y-4"
        >
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('ivrTrees.treePlaceholder')}
            maxLength={200}
          />
          {rename.isError && (
            <p role="alert" className="text-xs text-red-500">
              {rename.error instanceof Error ? rename.error.message : t('common.failedToSave')}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={rename.isPending}>{t('common.cancel')}</Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit || rename.isPending}>
              {rename.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────

function DeleteTreeDialog({ row, onClose }: { row: IvrTreeSummaryRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => ivrTrees.remove(row.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrTrees'] });
      onClose();
    },
  });
  const hasDids = row.did_count > 0;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('ivrTrees.deleteTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>{t('ivrTrees.deleteBody', { name: row.name })}</p>
          {hasDids && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs text-amber-800">
                {row.did_count === 1
                  ? t('ivrTrees.deleteDidWarning', { count: row.did_count })
                  : t('ivrTrees.deleteDidWarning_plural', { count: row.did_count })}
              </p>
            </div>
          )}
        </div>
        {del.isError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : t('common.failedToDelete')}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={del.isPending}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Restore dialog (recycle bin) ──────────────────────────────────────────────

function RestoreTreeDialog({ row, onClose }: { row: IvrTreeSummaryRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const restore = useMutation({
    mutationFn: () => ivrTrees.restore(row.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrTrees'] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Restore IVR tree?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          &ldquo;{row.name}&rdquo; will be restored to your trees. It comes back{' '}
          <strong>inactive</strong>, and you&rsquo;ll need to re-set its start (root) node
          before it can take calls again.
        </p>
        {restore.isError && (
          <p role="alert" className="text-xs text-red-500">
            {restore.error instanceof Error ? restore.error.message : t('common.failedToSave')}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={restore.isPending}>{t('common.cancel')}</Button>
          </DialogClose>
          <Button disabled={restore.isPending} onClick={() => restore.mutate()}>
            {restore.isPending ? 'Restoring…' : 'Restore'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Root-status chip — inline warning when tree has no root ───────────────────

function RootStatusChip({ row }: { row: IvrTreeSummaryRow }) {
  const { t } = useTranslation();
  if (!row.root_node_id) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
          'border-amber-200 bg-amber-50 text-amber-700',
        )}
        title={t('ivrTrees.noRoot')}
      >
        <AlertTriangle className="h-3 w-3" />
        {t('ivrTrees.noRoot')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
      <GitBranch className="h-3 w-3" />
      {t('ivrTrees.rootSet')}
    </span>
  );
}

// ── DID-count pill ────────────────────────────────────────────────────────────

function DidCountCell({ row }: { row: IvrTreeSummaryRow }) {
  const count = row.did_count;
  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Phone className="h-3 w-3" />
        —
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs tabular-nums text-slate-600"
      title={`${count} active phone ${count === 1 ? 'number routes' : 'numbers route'} to this tree`}
    >
      <Phone className="h-3 w-3 text-slate-400" />
      {count}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function IvrTreesList() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const effectiveCustomerId = useEffectiveCustomerId();

  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [renameRow, setRenameRow] = useState<IvrTreeSummaryRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<IvrTreeSummaryRow | null>(null);
  const [restoreRow, setRestoreRow] = useState<IvrTreeSummaryRow | null>(null);
  // 'active' = live trees, 'deleted' = recycle-bin (soft-deleted) trees.
  const [view, setView] = useState<'active' | 'deleted'>('active');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canMutate = CAN_MUTATE.includes(user.role);
  const showCustomerFilter = user.is_super_admin || user.role === 'reseller_owner';

  // When inside a customer context, effectiveCustomerId is the entered customer.
  // The explicit customerFilter dropdown (shown for SA/reseller_owner) is treated
  // as a secondary override; if empty, we fall back to the context customer ID.
  const resolvedCustomerId = customerFilter || effectiveCustomerId || undefined;

  function handleSearchChange(v: string) {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(v), 300);
  }

  const { data: customerList } = useQuery({
    queryKey: ['customers', 'tree-list-filter'],
    queryFn: () => customers.list({ limit: 200 }),
    enabled: showCustomerFilter,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['ivrTrees', view, debouncedSearch, resolvedCustomerId],
    queryFn: () => ivrTrees.list({
      search: debouncedSearch || undefined,
      customer_id: resolvedCustomerId,
      deleted: view === 'deleted',
    }),
  });

  const rows = data?.rows ?? [];

  // Columns are built dynamically so we can hide the "Customer" column for
  // office_manager (single-customer scope — redundant) but show it for
  // super_admin / reseller_owner, who see trees across many customers.
  const columns = [
    {
      header: '#',
      cell: (row: IvrTreeSummaryRow) => (
        <span className="font-mono tabular-nums text-xs text-slate-400">
          {row.ref_number != null ? `IVT-${row.ref_number}` : '—'}
        </span>
      ),
    },
    {
      header: t('ivrTrees.columnName'),
      cell: (row: IvrTreeSummaryRow) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-medium text-slate-900">{row.name}</span>
          {!row.active && (
            <span className="text-[10px] uppercase tracking-wider text-slate-400">{t('common.inactive')}</span>
          )}
        </div>
      ),
    },
    ...(showCustomerFilter ? [{
      header: t('ivrTrees.columnCustomer'),
      cell: (row: IvrTreeSummaryRow) => (
        <span className="text-sm text-slate-600">{row.customer_name}</span>
      ),
    }] : []),
    {
      header: t('ivrTrees.columnRouting'),
      cell: (row: IvrTreeSummaryRow) => <RootStatusChip row={row} />,
    },
    {
      header: t('ivrTrees.columnPhoneNumbers'),
      headClassName: 'hidden md:table-cell',
      className: 'hidden md:table-cell',
      cell: (row: IvrTreeSummaryRow) => <DidCountCell row={row} />,
    },
    {
      header: t('ivrTrees.columnNodes'),
      headClassName: 'hidden md:table-cell',
      className: 'hidden md:table-cell',
      cell: (row: IvrTreeSummaryRow) => (
        <span
          className="inline-flex items-center gap-1 text-xs tabular-nums text-slate-600"
          title={`${row.node_count}`}
        >
          <Workflow className="h-3 w-3 text-slate-400" />
          {row.node_count}
        </span>
      ),
    },
    {
      header: view === 'deleted' ? 'Deleted' : t('ivrTrees.columnStatus'),
      headClassName: 'hidden sm:table-cell',
      className: 'hidden sm:table-cell',
      cell: (row: IvrTreeSummaryRow) => (
        view === 'deleted'
          ? <span className="text-xs text-slate-400">{timeAgo(row.deleted_at ?? row.updated_at)}</span>
          : <StatusCell row={row} canMutate={canMutate} />
      ),
    },
    {
      header: t('ivrTrees.columnUpdated'),
      headClassName: 'hidden lg:table-cell',
      className: 'hidden lg:table-cell text-xs text-slate-400',
      cell: (row: IvrTreeSummaryRow) => (
        <span title={new Date(row.updated_at).toLocaleString()}>
          {timeAgo(row.updated_at)}
        </span>
      ),
    },
    {
      header: '',
      className: 'w-10 text-end',
      cell: (row: IvrTreeSummaryRow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('ivrTrees.rowActions')}
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {view === 'deleted' ? (
              canMutate ? (
                <DropdownMenuItem onClick={() => setRestoreRow(row)}>
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem disabled>No actions</DropdownMenuItem>
              )
            ) : (
              <>
                <DropdownMenuItem onClick={() => navigate(`/ivr/trees/${routeRef(row, 'IVT')}`)}>
                  <ExternalLink className="mr-2 h-3.5 w-3.5" />
                  {t('ivrTrees.openInCanvas')}
                </DropdownMenuItem>
                {canMutate && (
                  <>
                    <DropdownMenuItem onClick={() => setRenameRow(row)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      {t('ivrTrees.rename')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:bg-red-50 focus:text-red-600"
                      onClick={() => setDeleteRow(row)}
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Summary strip under the page header — counts (visible state, not filtered).
  const treesWithoutRoot = rows.filter((r) => !r.root_node_id && r.active).length;

  return (
    <>
      <PageHeader
        eyebrow={t('navGroups.telephony')}
        title={t('ivrTrees.title')}
        description={t('ivrTrees.description')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {showCustomerFilter && (
              <Select
                value={customerFilter || '__all__'}
                onValueChange={(v) => setCustomerFilter(v === '__all__' ? '' : v)}
              >
                <SelectTrigger className="h-9 w-44 text-sm">
                  <SelectValue placeholder={t('ivrTrees.allCustomers')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t('ivrTrees.allCustomers')}</SelectItem>
                  {(customerList?.rows ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('ivrTrees.searchPlaceholder')}
              className="h-9 w-48 text-sm"
              aria-label={t('ivrTrees.searchPlaceholder')}
            />
            {view === 'active' ? (
              <Button variant="outline" size="sm" onClick={() => setView('deleted')}>
                <Archive className="mr-1.5 h-4 w-4" />
                Recycle bin
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setView('active')}>
                <Workflow className="mr-1.5 h-4 w-4" />
                Back to trees
              </Button>
            )}
            {canMutate && view === 'active' && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                {t('ivrTrees.newTree')}
              </Button>
            )}
          </div>
        }
      />

      {/* Recycle-bin context banner. */}
      {view === 'deleted' && (
        <div className="mb-4 flex items-start gap-2.5 rounded-md border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600">
          <Archive className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <div>Deleted IVR trees. Restoring brings a tree back <strong>inactive</strong> with its nodes intact — re-set its start node, then activate it.</div>
        </div>
      )}

      {/* Attention banner — surfaces trees with no root node so they get fixed. */}
      {view === 'active' && !isLoading && treesWithoutRoot > 0 && (
        <div
          role="status"
          className="mb-4 flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-amber-900">
            {treesWithoutRoot === 1
              ? t('ivrTrees.activeTreesNoRoot', { count: treesWithoutRoot })
              : t('ivrTrees.activeTreesNoRoot_plural', { count: treesWithoutRoot })}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-400">{t('common.loading')}</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500">
          {error instanceof Error ? error.message : t('ivrTrees.failedToLoad')}
          <Button
            variant="outline"
            size="sm"
            className="ms-3"
            onClick={() => qc.invalidateQueries({ queryKey: ['ivrTrees'] })}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          onRowClick={view === 'deleted' ? undefined : (row) => navigate(`/ivr/trees/${routeRef(row, 'IVT')}`)}
          empty={
            view === 'deleted' ? (
              <div className="py-10 text-center">
                <Archive className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-500">Recycle bin is empty</p>
                <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
                  Deleted IVR trees show up here for restore.
                </p>
              </div>
            ) : debouncedSearch || customerFilter ? (
              <span>
                {t('ivrTrees.noTreesMatchFilters')}
                <Button
                  variant="link"
                  size="sm"
                  className="ms-1 h-auto p-0"
                  onClick={() => {
                    setSearch('');
                    setDebouncedSearch('');
                    setCustomerFilter('');
                  }}
                >
                  {t('ivrTrees.clearFilters')}
                </Button>
              </span>
            ) : (
              <div className="py-10 text-center">
                <Workflow className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-500">{t('ivrTrees.emptyState')}</p>
                <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
                  {t('ivrTrees.noTreesEmptyDescription')}
                </p>
                {canMutate && (
                  <Button size="sm" className="mt-4" onClick={() => setCreateOpen(true)}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    {t('ivrTrees.createFirstTree')}
                  </Button>
                )}
              </div>
            )
          }
        />
      )}

      <CreateTreeDialog open={createOpen} onOpenChange={setCreateOpen} />
      {renameRow && <RenameTreeDialog row={renameRow} onClose={() => setRenameRow(null)} />}
      {deleteRow && <DeleteTreeDialog row={deleteRow} onClose={() => setDeleteRow(null)} />}
      {restoreRow && <RestoreTreeDialog row={restoreRow} onClose={() => setRestoreRow(null)} />}
    </>
  );
}
